import React, { useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Upload, Loader2, Zap, FileCheck2, AlertTriangle, XCircle, ChevronRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  pass: { color: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/5", icon: CheckCircle2, label: "PASS" },
  warn: { color: "text-[#FFB000] border-[#FFB000]/40 bg-[#FFB000]/5", icon: AlertTriangle, label: "WARN" },
  fail: { color: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/5", icon: XCircle, label: "FAIL" },
  na: { color: "text-zinc-500 border-zinc-700 bg-zinc-900/30", icon: null, label: "N/A" },
};

const SCORE_COLOR = (s) => s >= 85 ? "text-[#00E676]" : s >= 65 ? "text-[#FFB000]" : "text-[#FF3B30]";

export default function PublicScorecardPreview() {
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [playbookId, setPlaybookId] = useState("");  // blank = auto-detect
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!file) { setError("Drop a CSV first."); return; }
    if (!email) { setError("Add a work email so we can send your scorecard."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("email", email);
      fd.append("company", company);
      fd.append("playbook_id", playbookId);
      const r = await axios.post(`${API}/public/scorecard-preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not process this CSV. Try a smaller file.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setFile(null); setError(""); };

  return (
    <section id="try" className="border-b border-zinc-900 py-24 relative overflow-hidden bg-[#040404]">
      <div className="grid-noise absolute inset-0 opacity-30" />
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">09 · TRY IT IN 60 SECONDS</div>
        <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl mb-4">
          Drop a CSV. <span className="text-[#FFB000]">Get a regulator-grade scorecard.</span>
        </h2>
        <p className="text-zinc-400 max-w-2xl mb-12 text-sm leading-relaxed">
          No signup. No demo call. Drop a sample of your real customer data (up to 200 rows) and we&apos;ll
          score it against the right playbook in under a minute. CBN, SARB, RBI — pick a market or let
          DARA auto-detect.
        </p>

        {!result ? (
          <form
            onSubmit={submit}
            className="grid lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900"
            data-testid="public-scorecard-form"
          >
            {/* LEFT — file dropzone */}
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              className={`bg-black p-10 cursor-pointer transition-colors ${file ? "bg-[#0f0b00]" : "hover:bg-[#080808]"}`}
              data-testid="public-scorecard-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="public-scorecard-file"
              />
              <Upload className={`w-8 h-8 mb-4 stroke-[1.5] ${file ? "text-[#FFB000]" : "text-zinc-600"}`} />
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// CSV UPLOAD</div>
              {file ? (
                <>
                  <div className="font-heading font-bold text-xl mb-1 text-[#FFB000] truncate">{file.name}</div>
                  <div className="font-mono text-[11px] text-zinc-500">{(file.size / 1024).toFixed(1)} KB · click to change</div>
                </>
              ) : (
                <>
                  <div className="font-heading font-bold text-2xl mb-2">Drop a CSV file here</div>
                  <div className="font-mono text-[11px] text-zinc-500 leading-relaxed">
                    Up to 200 rows · 256 KB · CRM, POS or transaction snapshots work best.<br />
                    We don&apos;t store your data — only the metadata (filename, row count, score).
                  </div>
                </>
              )}
            </div>

            {/* RIGHT — form fields */}
            <div className="bg-black p-10 space-y-5">
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">WORK EMAIL *</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="cdo@yourcompany.com"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white mt-1 focus:border-[#FFB000] focus:outline-none"
                  data-testid="public-scorecard-email"
                />
              </div>
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">COMPANY (OPTIONAL)</label>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="First Bank Nigeria"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white mt-1 focus:border-[#FFB000] focus:outline-none"
                  data-testid="public-scorecard-company"
                />
              </div>
              <div>
                <label className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">PLAYBOOK</label>
                <select
                  value={playbookId}
                  onChange={(e) => setPlaybookId(e.target.value)}
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-[12px] text-white mt-1 focus:border-[#FFB000] focus:outline-none"
                  data-testid="public-scorecard-playbook"
                >
                  <option value="">Auto-detect from data</option>
                  <option value="nigeria_banking_v1">🇳🇬 Nigeria Banking (CBN · NIBSS · EFInA)</option>
                  <option value="south_africa_banking_v1">🇿🇦 South Africa Banking (SARB · POPIA · FICA)</option>
                  <option value="india_fintech_v1">🇮🇳 India Fintech (RBI · NPCI · MSMED · ITD)</option>
                  <option value="australia_banking_v1">🇦🇺 Australia Banking (APRA · AUSTRAC · ATO · OAIC)</option>
                  <option value="brazil_pix_v1">🇧🇷 Brazil Pix (BACEN · LGPD · Receita)</option>
                </select>
              </div>

              {error ? (
                <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 px-3 py-2 font-mono text-[11px] text-[#FF3B30]" data-testid="public-scorecard-error">
                  {error}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={loading || !file}
                data-testid="public-scorecard-submit"
                className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-4 hover:bg-[#E59E00] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {loading ? "SCORING…" : "GET MY SCORECARD"}
              </button>
              <div className="font-mono text-[9px] text-zinc-600 text-center uppercase tracking-widest">
                NO CARD · NO SIGNUP · 1 PREVIEW PER EMAIL / 24H
              </div>
            </div>
          </form>
        ) : (
          <ScorecardResult result={result} email={email} onReset={reset} />
        )}
      </div>
    </section>
  );
}

function ScorecardResult({ result, email, onReset }) {
  return (
    <div className="border border-zinc-900 bg-black" data-testid="public-scorecard-result">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-6 border-b border-zinc-900 bg-[#0a0a0a]">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">
            {result.auto_selected ? "// AUTO-DETECTED PLAYBOOK" : "// PLAYBOOK"}
          </div>
          <div className="font-heading font-black text-2xl">{result.playbook_name}</div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">{result.regulator}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">OVERALL SCORE</div>
          <div className={`font-heading font-black text-6xl ${SCORE_COLOR(result.overall_score)} tabular-nums`}>
            {result.overall_score}
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">
            {result.applicable_rules} of {result.total_rules} rules applicable · {result.row_count} rows · {result.column_count} columns
          </div>
        </div>
      </div>

      {/* Top actions */}
      {result.top_actions?.length ? (
        <div className="p-6 border-b border-zinc-900">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">▸ TOP ACTIONS</div>
          <div className="space-y-3">
            {result.top_actions.map((a, i) => {
              const cfg = STATUS_PILL[a.status] || STATUS_PILL.na;
              return (
                <div key={i} className="border border-zinc-800 p-4">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${cfg.color}`}>
                      {cfg.label}
                    </span>
                    <span className="font-heading font-bold text-base">{a.title}</span>
                    {a.citation ? (
                      <span className="font-mono text-[9px] text-zinc-500 ml-auto">[{a.citation}]</span>
                    ) : null}
                  </div>
                  {a.recommendation ? (
                    <p className="text-sm text-zinc-400 leading-relaxed">{a.recommendation}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Full results */}
      <div className="p-6 border-b border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4">// ALL {result.total_rules} BENCHMARKS</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {result.results.map((r, i) => {
            const cfg = STATUS_PILL[r.status] || STATUS_PILL.na;
            return (
              <div key={i} className="border border-zinc-900 p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-heading font-bold text-sm truncate">{r.title}</span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${cfg.color} flex-shrink-0`}>
                    {cfg.label}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-zinc-500">
                  {r.observed ? `Observed: ${r.observed}` : r.reason ? r.reason : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="p-6 bg-[#0f0b00] border-t border-[#FFB000]/30 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FileCheck2 className="w-4 h-4 text-[#00E676]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">SCORECARD READY</span>
          </div>
          <div className="font-heading font-bold text-base">
            This is just a preview. The full DARA terminal scores files of any size — every week, automatically.
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">
            Sent a copy to <span className="text-zinc-300">{email}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onReset}
            data-testid="public-scorecard-reset"
            className="border border-zinc-700 text-white font-mono text-[11px] uppercase tracking-wider px-5 py-3 hover:bg-zinc-900"
          >
            Score another file
          </button>
          <Link
            to="/signup"
            data-testid="public-scorecard-signup"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-5 py-3 hover:bg-[#E59E00] flex items-center gap-2"
          >
            Deploy the terminal <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
